import {
  type Active,
  type ClientRect,
  type CollisionDescriptor,
  type CollisionDetection,
  type DragOverEvent,
  type DroppableContainer,
  type Modifier,
  type Over,
} from '@dnd-kit/core';
import type {
  ColumnPinningState,
  TableFeatures,
  Column,
  RowData,
  ReactTable,
} from '@tanstack/react-table';
import { TouchEvent } from 'react';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import * as styles from './ColumnOrder.sty.js';
import { getDataTableBorderWidth } from '../../../utils/data-table-border-width.js';
import {
  DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH,
  DATA_TABLE_DND_PLACEHOLDER_DIVIDER_CSS_VAR,
  DATA_TABLE_HORIZONTAL_DIVIDER_HEIGHT,
} from '../../constants.js';
import type { DragAndDropTransform } from '../types/dnd.types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

//#region DragAndDrop

// DOM Query for filtering out pinned parent headers
export const unpinnedHeaderFilter =
  ':not([data-headerid*="left"]):not([data-headerid*="right"])';
// Pixels from the scroll boundary at which custom auto-scroll begins.
export const SCROLL_PROXIMITY_ZONE = 10;
/** Offset for column drag-and-drop collision detection (px). */
const COLUMN_DND_COLLISION_OFFSET = DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH / 2 - 1;
/** Number of pixels above which a touch move event is considered a drag motion. */
const TOUCH_DRAG_MOTION_THRESHOLD = 5;
// Min custom scroll speed (px/frame) applied at the edge of the proximity zone. Start slow, then speed up to max scroll speed.
const MIN_SCROLL_SPEED = 3;
// Max custom scroll speed (px/frame) applied when fully past the boundary. 15 feels smooth, anything above feels too fast and anything below feels too slow.
const MAX_SCROLL_SPEED = 15;

/**
 * Determines if the touch interaction is a drag motion.
 *
 * @param touchMove - The current touch move event.
 * @param touchStart - The preceding touch start event.
 * @returns A boolean indicating whether the touch move event is considered a drag motion.
 * @internal
 */
export function isDragMotion(
  touchMove: TouchEvent,
  touchStart?: TouchEvent,
): boolean {
  const touchStartX = touchStart?.touches[0].clientX;
  if (touchStartX === undefined) {
    return false;
  }
  const touchMoveX = touchMove.touches[0].clientX;
  return Math.abs(touchMoveX - touchStartX) > TOUCH_DRAG_MOTION_THRESHOLD;
}

/**
 * Updates the column order state based on the active and over columns.
 *
 * @param active - The actively dragged item.
 * @param over - The droppable item which the active item is currently dragged over.
 * @param table - The table instance.
 * @param virtualizationContainer - The table's virtualization container.
 * @internal
 */
export function updateColumnOrderOnDragEnd(config: {
  active: Active;
  over: Over | null;
  table: ReactTable<TDataTableFeatures, RowData>;
  virtualizationContainer?: HTMLDivElement;
}): void {
  const { active, over, virtualizationContainer } = config;
  if (
    over === null ||
    active.data.current === undefined ||
    over.data.current === undefined ||
    virtualizationContainer === undefined
  ) {
    return;
  }

  const activeColumn = active.data.current.column as Column<
    TDataTableFeatures,
    RowData
  >;
  const activeChildColumnIds = activeColumn.columns.map((child) => child.id);
  const activeChildColumnsCount = activeChildColumnIds.length;
  const activeParentColumnId = activeColumn.parent?.id;

  const overColumn = over.data.current.column as Column<
    TDataTableFeatures,
    RowData
  >;
  const overParentColumnId = over.data.current?.column?.parent?.id;
  const overChildColumnIds = overColumn.columns.map((child) => child.id);
  const overChildColumnsCount = overChildColumnIds.length;

  // move columns if both columns have the same parent or no parent
  if (activeParentColumnId === overParentColumnId) {
    const columnOrder = config.table.atoms.columnOrder?.get() ?? [];
    const moveCount = Math.max(activeChildColumnsCount, 1);

    // get the active and over columns' indexes in the unmodified column order to consider hidden columns during reordering
    const activeColumnIndex = columnOrder.findIndex((columnId) => {
      const id =
        activeChildColumnsCount === 0
          ? activeColumn.id
          : // get the index of the first child column if the active column is a parent
            activeColumn.getLeafColumns()[0].id;

      return columnId === id;
    });
    const overColumnIndex = columnOrder.findIndex((columnId) => {
      const id =
        overChildColumnsCount === 0
          ? overColumn.id
          : // get the index of the first child column if the active column is a parent
            overColumn.getLeafColumns()[0].id;
      return columnId === id;
    });

    // update column order
    const newColumnOrder = [...columnOrder];
    const movedColumns = newColumnOrder.splice(activeColumnIndex, moveCount);
    // if moving more than one column to the right, toIndex needs to be adjusted since more than one column were spliced from the array
    const toIndex =
      overColumnIndex > activeColumnIndex
        ? overColumnIndex - (moveCount - 1)
        : overColumnIndex;

    newColumnOrder.splice(toIndex, 0, ...movedColumns);
    config.table.setColumnOrder(newColumnOrder);
  }

  config.table.setDraggedHeaderColumn(null);
  removeInsertPositionIndicator(lastStyledCells, virtualizationContainer, true);
}

/**
 * Gets the speed for the custom auto scroll.
 *
 * @param scrollZoneDepth - the mouse pointer position when penetrating the area where auto scroll should kick in
 *
 * @returns scroll speed measured in pixels per frame, a number between MIN_SCROLL_SPEED and MAX_SCROLL_SPEED
 * @internal
 */
export const getCustomScrollSpeed = (scrollZoneDepth: number): number => {
  return Math.round(
    MIN_SCROLL_SPEED +
      (Math.min(scrollZoneDepth, SCROLL_PROXIMITY_ZONE) /
        SCROLL_PROXIMITY_ZONE) *
        (MAX_SCROLL_SPEED - MIN_SCROLL_SPEED),
  );
};

/**
 * Creates a temporary DOM element that is temporarily inserted into the header group
 * during a drag operation to preserve the CSS grid layout when the dragged header cell is portalled out
 * of its original position.
 *
 * @param gridArea - dragged element's position in the grid
 * @param width - dragged element's width
 * @param height - dragged element's height
 * @param verticalDividerOptions - optional config for the vertical divider styling on the placeholder
 *
 * @internal
 */
export const createHeaderPlaceholder = (
  gridArea: string,
  width: number,
  height: number,
  verticalDividerOptions?: {
    isParentColumn: boolean;
    heightOffset: number;
  },
): HTMLDivElement => {
  const placeholder = document.createElement('div');
  placeholder.style.gridArea = gridArea;
  placeholder.style.width = `${width}px`;
  placeholder.style.height = `${height}px`;

  if (verticalDividerOptions) {
    const { isParentColumn, heightOffset } = verticalDividerOptions;
    placeholder.classList.add(styles.verticalDividers);
    placeholder.style.setProperty(
      DATA_TABLE_DND_PLACEHOLDER_DIVIDER_CSS_VAR,
      !isParentColumn && heightOffset !== 0
        ? `${heightOffset}px`
        : Spacings.Size4,
    );
  }

  return placeholder;
};

//#endregion DragAndDrop

//#region PositionIndicator
// save the cells that last received the indicator styles to be able to remove them again
const lastStyledCells = new Set<HTMLElement>();

/**
 * Transforms columns left/right to make space for the column which is currently being dragged ("active" column).
 *
 * @param event - contains "active" (column being dragged) and "over" columns (column where the active column is currently dragged over).
 * @param virtualizationContainer - The table's virtualization container.
 * @internal
 */
export function indicateInsertPosition<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(event: DragOverEvent, virtualizationContainer?: HTMLDivElement): void {
  if (virtualizationContainer === undefined || !event.active || !event.over) {
    return;
  }

  const activeColumnIndex = event.active.data.current?.columnIndex;
  const overColumnId = event.over.id as string;
  const overColumnIndex = event.over.data.current?.columnIndex;

  // If the over column is a parent, we also need to render the indicator for the first/last unpinned child
  // depending on whether the active column is moved left or right
  const overColumnChildren = (
    event.over.data.current?.column as Column<TFeatures, TData>
  )
    .getVisibleLeafColumns()
    .filter(
      (child) =>
        !(
          child as unknown as Column<TDataTableFeatures, RowData>
        ).getIsPinned(),
    );
  const overFirstChildId = overColumnChildren.at(0)?.id;
  const overLastChildId = overColumnChildren.at(-1)?.id;

  if (activeColumnIndex < overColumnIndex) {
    // move right
    const overChildColumnId =
      overLastChildId !== overColumnId ? overLastChildId : undefined;
    renderInsertPositionIndicator(
      virtualizationContainer,
      overColumnId,
      overChildColumnId,
      'right',
    );
  } else if (activeColumnIndex > overColumnIndex) {
    // move left
    const overChildColumnId =
      overFirstChildId !== overColumnId ? overFirstChildId : undefined;
    renderInsertPositionIndicator(
      virtualizationContainer,
      overColumnId,
      overChildColumnId,
      'left',
    );
  } else {
    removeInsertPositionIndicator(lastStyledCells);
  }
}

/**
 * Renders an indicator at the position where the column will be inserted.
 */
function renderInsertPositionIndicator(
  virtualizationContainer: HTMLDivElement,
  columnId: string,
  childColumnId: string | undefined,
  position: 'left' | 'right',
): void {
  const overCell = getHeaderCell(virtualizationContainer, columnId);
  if (overCell === null) {
    return;
  }

  const overChildColumnCell = getHeaderCell(
    virtualizationContainer,
    childColumnId,
  );

  // remove previous styles
  removeInsertPositionIndicator(lastStyledCells);
  // hide resize handles so they don't overlap the indicators
  const resizeHandles = getResizeHandles(virtualizationContainer);
  for (const resizeHandle of resizeHandles) {
    resizeHandle.style.display = 'none';
  }

  // add indicator after element
  addPositionIndicatorStyle(overCell, position);
  lastStyledCells.add(overCell);

  // add indicator also to child column if there is one
  if (overChildColumnCell) {
    addPositionIndicatorStyle(overChildColumnCell, position);
    lastStyledCells.add(overChildColumnCell);
  }
}

function addPositionIndicatorStyle(
  elem: HTMLElement,
  position: 'left' | 'right',
): void {
  elem.classList.add(
    styles.insertPositionIndicator.classNames.base,
    styles.insertPositionIndicator.classNames.variants.left[
      position === 'left' ? 'true' : 'false'
    ],
  );
}

/**
 * Removes the insert position indicator from the specified cells.
 */
function removeInsertPositionIndicator(
  cells: Set<HTMLElement>,
  virtualizationContainer: HTMLDivElement | undefined = undefined,
  displayResizeHandles: boolean = false,
): void {
  if (cells.size <= 0) {
    return;
  }

  if (displayResizeHandles && virtualizationContainer) {
    const resizeHandles = getResizeHandles(virtualizationContainer);
    for (const resizeHandle of resizeHandles) {
      resizeHandle.style.removeProperty('display');
    }
  }

  for (const cell of cells) {
    cell.classList.remove(
      styles.insertPositionIndicator.classNames.base,
      styles.insertPositionIndicator.classNames.variants.left.true,
      styles.insertPositionIndicator.classNames.variants.left.false,
    );
    lastStyledCells.delete(cell);
  }
}

/**
 * Gets all cells with the given column id.
 * @param virtualizationContainer - The table's virtualization container.
 * @param columnId - id of the column (matches against `data-columnid`).
 * @returns matching cells.
 */
function getHeaderCell(
  virtualizationContainer: HTMLDivElement,
  columnId?: string,
): HTMLElement | null {
  if (columnId === undefined) {
    return null;
  }

  return virtualizationContainer.querySelector<HTMLElement>(
    `[role="columnheader"][data-headerid=${CSS.escape(
      columnId,
    )}], [role='columnheader'][data-columnid="${CSS.escape(columnId)}"]`,
  );
}

/**
 * Gets all resize handles in the table.
 * @param virtualizationContainer - The table's virtualization container.
 * @returns matching elements.
 */
function getResizeHandles(
  virtualizationContainer: HTMLDivElement,
): HTMLElement[] {
  const resizeHandles = virtualizationContainer.querySelectorAll<HTMLElement>(
    '[data-trigger="resize"]',
  );
  return Array.from(resizeHandles);
}

//#endregion PositionIndicator

//#region CollisionDetection
/**
 * Check if a given point is contained within a bounding rectangle
 */
function isPointWithinRectX(pointX: number, rect: ClientRect): boolean {
  return rect.left <= pointX && pointX <= rect.right;
}

/**
 * Custom collision detection algorithm for column order drag and drop.
 * Determines the closest droppable target within the X-axis based on the dragging direction.
 *
 * @param param0 - Object containing the droppable containers, the droppable rects, the collision rect and
 *                 the active element, which are needed for the collision detection.
 * @returns An array of collision descriptors, sorted by the distance to the dragged column, or an empty array if none is found.
 */
const closestTargetWithinX: CollisionDetection = ({
  droppableContainers,
  droppableRects,
  active,
  collisionRect,
}): CollisionDescriptor[] => {
  const initialRect = active.rect.current.initial;

  if (initialRect === null) {
    return [];
  }

  const collisions: CollisionDescriptor[] = [];

  // To make this work for varying column widths, depending on the dragging direction
  // we take either the left or the right of the dragged column to check for collisions
  // (minus a small offset, so the columns have to overlap a bit, before they would be swapped)
  const isDraggingLeft = collisionRect.left < initialRect.left;
  // min column width is 30px, to ensure the offset is in the left/right half: 30 / 2 - 1 = 14
  const collisionRectX = isDraggingLeft
    ? collisionRect.left + COLUMN_DND_COLLISION_OFFSET
    : collisionRect.right - COLUMN_DND_COLLISION_OFFSET;

  for (const droppableContainer of droppableContainers) {
    const { id } = droppableContainer;
    const rect = droppableRects.get(id);

    if (rect === undefined) {
      continue;
    }

    if (isPointWithinRectX(collisionRectX, rect)) {
      // return the container that the active rect is dragged over
      return [
        {
          id,
          data: { droppableContainer, value: 0 },
        },
      ];
    }

    // in case the dragged column's collisionRectX is not directly within any of the droppable containers
    // (e.g. when dragging over the scrollbar) we need to find the closest one. Therefore, we calculate
    // the distance between the droppable container's top/bottom and the collisionRectX coordinate.
    const droppableContainerX = isDraggingLeft ? rect.left : rect.right;
    const distance = Math.abs(collisionRectX - droppableContainerX);
    collisions.push({ id, data: { droppableContainer, value: distance } });
  }

  return collisions.sort((a, b) => a.data.value - b.data.value);
};

/**
 * Custom collision detection function that filters droppable containers based on the type of column being dragged,
 * and then applies the custom closestCenterWithinX algorithm on the filtered targets.
 *
 * @param active - The actively dragged item.
 * @param droppableContainers - The list of all droppable containers.
 * @param rest - Additional collision detection parameters.
 * @internal
 */
export const customCollisionDetection: CollisionDetection = ({
  active,
  droppableContainers,
  ...rest
}): CollisionDescriptor[] => {
  if (active.data.current === undefined) {
    return [];
  }

  const activeParentColumnId = active.data.current?.column?.parent?.id;
  let validTargets: DroppableContainer[];

  // Filter droppable containers based on the type of column being dragged
  if (activeParentColumnId === undefined) {
    // Filter out child columns
    validTargets = droppableContainers.filter((target) => {
      const targetIsChildColumn =
        target.data.current?.column?.parent !== undefined;
      return !targetIsChildColumn;
    });
  } else {
    // Only consider sibling child columns
    validTargets = droppableContainers.filter((target) => {
      const targetParentColumnId = target.data.current?.column?.parent?.id;
      return targetParentColumnId === activeParentColumnId; // Only siblings are valid
    });
  }

  // Filter builtin, pinned columns, and pinned parent header fragments, as they are not valid drop targets.
  // When a parent column has some children pinned, tanstack-table creates separate header objects
  // per pinning group (e.g. "right_0_col-0_col-0-c"). These fragments should not be valid drop targets.
  validTargets = validTargets.filter((target) => {
    const id = String(target.id);
    if (id.startsWith('left_') || id.startsWith('right_')) {
      return false;
    }

    const column = target.data.current?.column as
      | Column<TDataTableFeatures, RowData>
      | undefined;

    if (!column) {
      return false;
    }

    if (column.columnDef.meta?.builtin === true) {
      return false;
    }

    // A parent column that still has unpinned children is a valid drop target
    const childColumns = column?.columns ?? [];
    if (childColumns.length > 0) {
      return childColumns.some((child) => !child.getIsPinned());
    }

    return !column.getIsPinned();
  });

  // Apply closestCenterWithinX on filtered targets and cast to the expected type
  return closestTargetWithinX({
    active,
    droppableContainers: validTargets,
    ...rest,
  }) as CollisionDescriptor[];
};
//#endregion CollisionDetection

//#region Modifiers
/**
 * Retrieves the left and right bounds of a column header.
 *
 * @param columnId -
 * @param virtualizationContainer - table's virtualization container.
 * @param rowIndex - Header row to query. Defaults to 0 (parent header).
 * @param centerOnly - Exclude pinned headers if true. Relevant for parents whose children span
 * multiple pinning groups, where we only need the center fragment's bounds.
 */
function getHeaderBounds(
  columnId: string,
  virtualizationContainer: HTMLDivElement,
  rowIndex: 0 | 1 = 0,
  centerOnly: boolean = false,
): {
  left?: number;
  right?: number;
  height?: number;
} {
  const element = virtualizationContainer.querySelector<HTMLElement>(
    `[role="columnheader"][data-header-row="${rowIndex}"][data-columnId=${CSS.escape(
      columnId,
    )}]${centerOnly ? unpinnedHeaderFilter : ''}`,
  );

  if (element) {
    const { left, right, height } = element.getBoundingClientRect();
    return { left, right, height };
  }

  return {
    left: undefined,
    right: undefined,
    height: undefined,
  };
}

/**
 * Get table bounds based on table variant, header height and virtualization container
 * @returns A custom dnd kit modifier.
 */
const getTableBounds = (
  containedVariant: boolean,
  virtualizationContainer: HTMLDivElement,
  totalHeaderHeight: number,
): {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
} => {
  const parentRect = virtualizationContainer.getBoundingClientRect();

  return {
    left: containedVariant
      ? parentRect.left + getDataTableBorderWidth()
      : parentRect.left,
    right: containedVariant
      ? parentRect.right - getDataTableBorderWidth()
      : parentRect.right,
    top: containedVariant
      ? parentRect.top + getDataTableBorderWidth()
      : parentRect.top,
    bottom: parentRect.bottom - totalHeaderHeight,
  };
};

/**
 * Retrieves the left and right bounds of the draggable area, constrained by any pinned columns
 * @internal
 */
export function getDraggableAreaBounds(
  pinnedColumns: ColumnPinningState,
  virtualizationContainer?: HTMLDivElement,
  rowIndex: 0 | 1 = 0,
): { left: number | undefined; right: number | undefined } {
  let left: number | undefined;
  let right: number | undefined;

  const leftPinnedColumns = pinnedColumns.left;
  const rightPinnedColumns = pinnedColumns.right;

  if (virtualizationContainer) {
    if (leftPinnedColumns?.length) {
      const lastLeftPinnedColumn =
        leftPinnedColumns[leftPinnedColumns.length - 1];

      // left bound should be the right position of the last column pinned to the left
      left = getHeaderBounds(
        lastLeftPinnedColumn,
        virtualizationContainer,
        rowIndex,
      ).right;
    }

    // right bound should be the left position of the first column pinned to the right
    if (rightPinnedColumns?.length) {
      right = getHeaderBounds(
        rightPinnedColumns[0],
        virtualizationContainer,
        rowIndex,
      ).left;
    }
  }

  return { left, right };
}

/**
 * Gets the header element that is being dragged.
 * @internal
 */
export function getDraggedHeaderElement(
  headerId: string,
  virtualizationContainer?: HTMLDivElement,
): {
  headerGroupElement: Element | null;
  draggedHeaderElement: Element | null | undefined;
} {
  if (!virtualizationContainer) {
    return { headerGroupElement: null, draggedHeaderElement: null };
  }

  const headerGroupElement =
    virtualizationContainer.querySelector('[role="rowgroup"]');

  const draggedHeaderElement = headerGroupElement?.querySelector(
    `[data-headerid="${CSS.escape(headerId)}"]`,
  );

  return { headerGroupElement, draggedHeaderElement };
}

/**
 * Restricts the horizontal movement of an element within the specified bounds.
 *
 * @param transform - Transform values of the currently dragged element
 * @param rect - The bounding rectangle of the element being transformed.
 * @param tableBounds - The bounds within which the element should be restricted.
 * @returns The updated transform object with restricted x value.
 * @internal
 */
export function restrictToTableBounds(
  transform: DragAndDropTransform,
  rect: ClientRect,
  tableBounds: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  },
): DragAndDropTransform {
  const value = {
    ...transform,
  };

  // horizontal restriction (only apply if bound is defined)
  if (typeof tableBounds.left === 'number') {
    if (rect.left + transform.x <= tableBounds.left) {
      value.x = tableBounds.left - rect.left;
    }
  }

  if (typeof tableBounds.right === 'number') {
    if (rect.right + transform.x >= tableBounds.right) {
      value.x = tableBounds.right - rect.right;
    }
  }

  // vertical restriction (only apply if bound is defined)
  if (typeof tableBounds.top === 'number') {
    if (rect.top + transform.y <= tableBounds.top) {
      value.y = tableBounds.top - rect.top;
    }
  }

  if (typeof tableBounds.bottom === 'number') {
    if (rect.bottom + transform.y >= tableBounds.bottom) {
      value.y = tableBounds.bottom - rect.bottom;
    }
  }

  return value;
}

/**
 * Creates a custom modifier to restrict drag and drop for column ordering.
 * This function ensures that columns cannot be dragged outside the table container,
 * and that child columns cannot be dragged past their parent.
 * If the dragging element would be moved past the desired bounds, the movement
 * coordinates will be manually adjusted accordingly.
 *
 * @param totalHeaderHeight - The height of the table header that is subtracted from the table height.
 * @param containedVariant - Whether the table has the `contained` variant set.
 * @param pinnedColumns - pinned columns state.
 * @param hasSubHeaders - Whether the table has nested columns.
 * @param virtualizationContainer - The table's virtualization container.
 * @returns A custom dnd kit modifier.
 * @internal
 */
export const createRestrictColumnDragAndDrop = (
  totalHeaderHeight: number,
  containedVariant: boolean,
  pinnedColumns: ColumnPinningState,
  hasSubHeaders: boolean,
  virtualizationContainer?: HTMLDivElement,
): Modifier => {
  return ({ transform, active, draggingNodeRect }) => {
    if (!draggingNodeRect || virtualizationContainer === undefined) {
      return transform;
    }

    const activeColumnParentId = active?.data.current?.column?.parent?.id;
    const hasChildColumns =
      activeColumnParentId === undefined &&
      active?.data.current?.column?.columns.length > 0;
    const isChildColumn = activeColumnParentId !== undefined;
    const hasPinnedColumns = !!(
      pinnedColumns.left?.length ?? pinnedColumns.right?.length
    );
    const headerRowIndex = hasSubHeaders ? 1 : 0;

    const tableBounds = getTableBounds(
      containedVariant,
      virtualizationContainer,
      totalHeaderHeight,
    );

    let leftPinnedAreaBound: number | undefined,
      rightPinnedAreaBound: number | undefined;

    if (hasPinnedColumns) {
      const { left, right } = getDraggableAreaBounds(
        pinnedColumns,
        virtualizationContainer,
        headerRowIndex,
      );

      leftPinnedAreaBound = left;
      rightPinnedAreaBound = right;
      tableBounds.left = left ?? tableBounds.left;
      tableBounds.right = right ?? tableBounds.right;
    }

    /**
     * Update table bounds for parent/nested columns
     */
    const updateTableBounds = (
      left?: number,
      right?: number,
      height?: number,
    ): void => {
      // This check is necessary because the height of nested headers is smaller than the total header height
      if (height !== undefined && tableBounds) {
        const horizontalDividerHeight = hasChildColumns
          ? DATA_TABLE_HORIZONTAL_DIVIDER_HEIGHT
          : 0;
        // Ensure bottom is defined before incrementing to satisfy TypeScript
        tableBounds.bottom =
          (tableBounds.bottom ?? 0) + height + horizontalDividerHeight;
      }

      if (isChildColumn && left !== undefined && right !== undefined) {
        tableBounds.left = Math.max(left, leftPinnedAreaBound ?? left);
        tableBounds.right = Math.min(right, rightPinnedAreaBound ?? right);
      }
    };

    // If this is a parent or child column, the vertical/horizontal bounds need to be adjusted
    if (hasChildColumns || isChildColumn) {
      const activeColumnId = active?.data.current?.column?.id;
      const columnIdForMeasuringHeaderBounds = hasChildColumns
        ? activeColumnId
        : activeColumnParentId;

      const { left, right, height } = getHeaderBounds(
        columnIdForMeasuringHeaderBounds,
        virtualizationContainer,
        0,
        isChildColumn && hasPinnedColumns,
      );

      // If this is a parent or child column, vertical dragging should be restricted to the bottom of the table.
      // for child columns, horizontal dragging should be restricted to the parent header
      updateTableBounds(left, right, height);
    }

    return restrictToTableBounds(transform, draggingNodeRect, tableBounds);
  };
};
//#endregion Modifiers
